import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { twJoin, twMerge } from "tailwind-merge";

import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { useCaseBlockScrollTarget } from "src/cases/caseView/CaseBlockScrollContext";
import { Icon } from "src/design-system/Icon";

export type CaseBlockPosition = {
  column: number;
  row: number;
  width: number;
  height: number;
};

export type CaseBlockProps = {
  title: string;
  icon?: IconDefinition | null;
  rightSlot?: ReactNode;
  leftSlot?: ReactNode;
  children: ReactNode;
  position: CaseBlockPosition;
  noPaddings?: boolean;
  isLoading?: boolean;
  dataLoc?: string;
  blockKey: string;
  noScroll?: boolean;
};

export const CaseBlock: React.FC<CaseBlockProps> = ({
  title,
  icon,
  rightSlot,
  leftSlot,
  children,
  position,
  noPaddings = false,
  isLoading = false,
  dataLoc = "case-block",
  blockKey,
  noScroll = false,
}) => {
  const [isBlinking, triggerBlink] = useCaseBlockBlink();

  const { ref: blockRef } = useCaseBlockScrollTarget<HTMLDivElement>(blockKey, {
    onScrollEnd: triggerBlink,
  });

  const gridPosition = {
    gridColumn: `${position.column} / span ${position.width}`,
    gridRow: `${position.row} / span ${position.height}`,
  };

  return (
    <div
      ref={blockRef}
      className={twMerge(
        "flex scroll-mt-3 flex-col rounded-lg border border-gray-200 bg-white transition-all duration-300",
        isBlinking && "border-indigo-300 shadow-gray-200 shadow-lg",
      )}
      data-loc={isLoading ? `${dataLoc}-loading` : dataLoc}
      style={gridPosition}
    >
      <div
        className="flex items-center gap-2 border-gray-200 border-b p-4"
        data-loc={dataLoc ? `${dataLoc}-header` : "case-block-header"}
      >
        {icon && (
          <Icon
            variant="hover"
            dataLoc={dataLoc ? `${dataLoc}-icon` : "case-block-icon"}
            icon={icon}
            size="2xs"
          />
        )}
        <h3 className="flex-1 truncate font-inter-semibold-13px text-gray-900">
          {title}
        </h3>
        {leftSlot && (
          <div
            className="flex items-center"
            data-loc={dataLoc ? `${dataLoc}-left-slot` : "case-block-left-slot"}
          >
            {leftSlot}
          </div>
        )}
        {rightSlot && (
          <div
            className="flex items-center"
            data-loc={
              dataLoc ? `${dataLoc}-right-slot` : "case-block-right-slot"
            }
          >
            {rightSlot}
          </div>
        )}
      </div>
      <div
        className={twJoin(
          "flex flex-1 flex-col",
          noScroll ? "overflow-hidden" : "decideScrollbar overflow-auto",
          !noPaddings && "p-4",
        )}
        data-loc={dataLoc ? `${dataLoc}-content` : "case-block-content"}
      >
        {isLoading ? (
          <div className={twJoin("flex flex-col gap-4", noPaddings && "p-4")}>
            <SkeletonPlaceholder height="h-6" width="w-full" />
            <SkeletonPlaceholder height="h-6" width="w-full" />
            <SkeletonPlaceholder height="h-6" width="w-full" />
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
};

const useCaseBlockBlink = () => {
  const [isBlinking, setIsBlinking] = useState<boolean>(false);
  const timeoutRef = useRef<number | null>(null);

  const triggerBlink = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
    }

    setIsBlinking(true);

    timeoutRef.current = window.setTimeout(() => {
      setIsBlinking(false);
      timeoutRef.current = null;
    }, 600);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return [isBlinking, triggerBlink] as const;
};
